"use client"

import { useEffect, useState } from "react"
import { Form, Modal } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { Search, Trash2, Users, Pin, Building, Eye, ChevronDown, Calendar } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { fetchThreads, fetchRecipients, deleteThread } from "../../redux/slices/messagesSlice"
import { timestampToISOString } from "../../utils/timestamp"
import { compareBuildingNames } from "../../data/buildings"
import { useToast } from "../../components/ui/use-toast"
import { useAuth } from "../../contexts/auth-context"
import { PlusButton } from "../../components/ui/plus-button"

export default function MessagesPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { threads = [], observedThreads = [], isLoading } = useAppSelector((state) => state.messages)
  const { user } = useAuth()
  const { toast } = useToast()
  const [searchQuery, setSearchQuery] = useState("")
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; threadId: string; participantName: string }>({
    show: false,
    threadId: "",
    participantName: "",
  })
  const navigate = useNavigate()

  const isAdmin = user?.role === "admin"
  const isSuperAdmin = user?.isSuperAdmin

  // Observed threads filters
  const [observedDays, setObservedDays] = useState(30)
  const [observedDateFrom, setObservedDateFrom] = useState("") // YYYY-MM-DD
  const [observedDateTo, setObservedDateTo] = useState("") // YYYY-MM-DD
  const [observedBuildingFilter, setObservedBuildingFilter] = useState("all")
  const [showDateFilter, setShowDateFilter] = useState(false)

  const getObservedFromISO = () => {
    if (observedDateFrom) {
      // Use start of the selected day in local time
      return new Date(observedDateFrom + "T00:00:00").toISOString()
    }
    const d = new Date()
    d.setDate(d.getDate() - observedDays)
    return d.toISOString()
  }

  // Fetch threads and recipients when component mounts or filter changes
  useEffect(() => {
    const params = isSuperAdmin ? { observedFrom: getObservedFromISO() } : undefined
    dispatch(fetchThreads(params))
    dispatch(fetchRecipients())
  }, [dispatch, observedDays, observedDateFrom, isSuperAdmin])

  const handleDeleteThread = async (threadId: string) => {
    try {
      const result = await dispatch(deleteThread(threadId)).unwrap()
      if (result.success) {
        toast({
          title: "Success",
          description: "Conversation deleted successfully",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete conversation",
        variant: "destructive",
      })
    } finally {
      setDeleteConfirm({ show: false, threadId: "", participantName: "" })
    }
    const params = isSuperAdmin ? { observedFrom: getObservedFromISO() } : undefined
    dispatch(fetchThreads(params))
  }

  const filteredThreads = threads
    ? threads.filter(
        (thread) =>
          thread.participantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          thread.lastMessage.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : []

  // Unique buildings from observed threads for filter dropdown
  const observedBuildings = Array.from(
    new Set(observedThreads.map((t) => t.participantBuilding).filter((b): b is string => Boolean(b))),
  ).sort(compareBuildingNames)

  const filteredObservedThreads = observedThreads
    ? observedThreads.filter((thread) => {
        const matchesSearch =
          thread.participantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          thread.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
        if (!matchesSearch) return false
        if (observedBuildingFilter !== "all" && thread.participantBuilding !== observedBuildingFilter) {
          return false
        }
        if (observedDateTo) {
          const dateStr = timestampToISOString(thread.lastMessageDate)
          if (dateStr) {
            const threadDate = new Date(dateStr)
            const toDate = new Date(observedDateTo + "T23:59:59")
            if (threadDate > toDate) return false
          }
        }
        return true
      })
    : []

  if (isLoading && threads.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-white" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4" style={{ height: "64px" }}>
        <div className="d-flex align-items-center gap-3">
          <h1 className="fs-2 fw-bold mb-0 text-white">{t("messages")}</h1>
          <Users className="text-white" size={32} />
        </div>
        <PlusButton variant="floating" to="/dashboard/messages/new" />
      </div>

      <div className="mb-4">
        <div className="position-relative">
          <div
            className="d-flex align-items-center gap-3 p-4 rounded-4"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Search className="text-white opacity-75" size={24} />
            <Form.Control
              type="search"
              placeholder={t("searchMessages")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-0 text-white fs-5 p-0"
              style={{
                outline: "none",
                boxShadow: "none",
                color: "white",
                "--bs-form-control-color": "white",
              }}
            />
            <style jsx>{`
              .form-control::placeholder {
                color: rgba(255, 255, 255, 0.7) !important;
                opacity: 1;
              }
            `}</style>
          </div>
        </div>
      </div>

      <div className="d-flex flex-column gap-3">
        {filteredThreads.length === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center p-5 rounded-4 text-white"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <p className="mb-0 opacity-75">{t("noConversations")}</p>
          </div>
        ) : (
          filteredThreads.map((thread) => (
            <div key={thread.id} className="position-relative">
              <Link to={`/dashboard/messages/${thread.id}`} className="text-decoration-none">
                <div
                  className="card-gradient p-4 rounded-4 position-relative"
                  style={{
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)"
                  }}
                >
                  <div className="d-flex align-items-start justify-content-between">
                    <div className="d-flex align-items-start gap-3 flex-grow-1">
                      <div className="position-relative flex-shrink-0">
                        {thread.type === "building-wide" ? (
                          <div
                            className="d-flex align-items-center justify-content-center bg-white text-primary rounded-circle"
                            style={{ width: 40, height: 40 }}
                          >
                            <Users size={20} />
                          </div>
                        ) : (
                          <img
                            src={thread.participantPhoto || "/placeholder.svg"}
                            alt={thread.participantName}
                            width={40}
                            height={40}
                            className="rounded-circle"
                          />
                        )}
                        {thread.isPinned && (
                          <div
                            className="position-absolute bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 16,
                              height: 16,
                              top: -2,
                              right: -2,
                              fontSize: "10px",
                            }}
                          >
                            <Pin size={10} />
                          </div>
                        )}
                      </div>

                      <div className="flex-grow-1">
                        <div className="d-none d-md-flex align-items-center gap-2 mb-1">
                          <h3 className="text-white mb-0 fs-4 fw-medium">{thread.participantName}</h3>
                          {thread.participantCount && (
                            <span className="badge bg-white text-primary small">
                              {thread.participantCount} {t("members")}
                            </span>
                          )}
                          {thread.unread && <span className="badge bg-warning text-dark">{t("unread")}</span>}
                        </div>

                        <div className="d-md-none">
                          <h3 className="text-white mb-2 fs-4 fw-medium">{thread.participantName}</h3>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            {thread.participantCount && (
                              <span className="badge bg-white text-primary small">
                                {thread.participantCount} {t("members")}
                              </span>
                            )}
                            {thread.unread && <span className="badge bg-warning text-dark">{t("unread")}</span>}
                          </div>
                          <div className="text-white mb-2">
                            <div className="opacity-75 small">
                              {(() => {
                                const dateString = timestampToISOString(thread.lastMessageDate)
                                if (!dateString) return "Unknown"
                                const date = new Date(dateString)
                                const time = date.toLocaleTimeString("el-GR", {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                const dateStr = date.toLocaleDateString("el-GR", {
                                  month: "numeric",
                                  day: "numeric",
                                  year: "numeric",
                                })
                                return `${time} ${dateStr}`
                              })()}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 text-white opacity-75 small mb-2">
                          {thread.participantBuilding && (
                            <span>
                              <Building size={12} className="me-1" />
                              {thread.participantBuilding}
                            </span>
                          )}
                          {thread.participantRoomNumber && (
                            <span>
                              • {t("room")} {thread.participantRoomNumber}
                            </span>
                          )}
                        </div>

                        <div
                          className="text-white opacity-75 mb-2"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            wordWrap: "break-word",
                            maxWidth: "100%",
                          }}
                        >
                          {thread.lastMessage}
                        </div>
                      </div>

                      <div className="d-none d-md-block text-white text-end ms-3" style={{ minWidth: "100px" }}>
                        <div className="fs-6 fw-medium mb-1">
                          {(() => {
                            const dateString = timestampToISOString(thread.lastMessageDate)
                            if (!dateString) return "Unknown"
                            const date = new Date(dateString)
                            return date.toLocaleTimeString("el-GR", {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })
                          })()}
                        </div>
                        <div className="opacity-75 small">
                          {(() => {
                            const dateString = timestampToISOString(thread.lastMessageDate)
                            if (!dateString) return "Unknown"
                            const date = new Date(dateString)
                            return date.toLocaleDateString("el-GR", {
                              month: "numeric",
                              day: "numeric",
                              year: "numeric",
                            })
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>

              {isAdmin && thread.type !== "building-wide" && (
                <button
                  className="position-absolute"
                  style={{
                    bottom: "16px",
                    right: "16px",
                    background: "#dc3545",
                    border: "none",
                    borderRadius: "8px",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setDeleteConfirm({
                      show: true,
                      threadId: thread.id,
                      participantName: thread.participantName,
                    })
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#c82333"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#dc3545"
                  }}
                  title={t("deleteConversation")}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Observed Threads Section (Super Admin) */}
      {isSuperAdmin && (
        <>
          <div className="d-flex align-items-center justify-content-between mt-4 mb-3">
            <div className="d-flex align-items-center gap-2">
              <Eye className="text-white opacity-75" size={20} />
              <h2 className="fs-5 fw-semibold mb-0 text-white opacity-75">{t("observedConversations")}</h2>
              <span className="badge bg-white bg-opacity-25 text-white small ms-1">
                {filteredObservedThreads.length}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              {observedBuildings.length > 1 && (
                <Form.Select
                  size="sm"
                  value={observedBuildingFilter}
                  onChange={(e) => setObservedBuildingFilter(e.target.value)}
                  className="border-0 rounded"
                  style={{
                    maxWidth: "160px",
                    background: "rgba(255, 255, 255, 0.15)",
                    color: "white",
                    fontSize: "0.8rem",
                  }}
                >
                  <option value="all" style={{ color: "#333" }}>{t("allBuildings")}</option>
                  {observedBuildings.map((b) => (
                    <option key={b} value={b} style={{ color: "#333" }}>{b}</option>
                  ))}
                </Form.Select>
              )}
              <button
                className="btn btn-sm text-white d-flex align-items-center gap-1"
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                }}
                onClick={() => setShowDateFilter(!showDateFilter)}
              >
                <Calendar size={14} />
                <span className="small">{t("filter")}</span>
              </button>
              {!observedDateFrom && observedDays < 365 && (
                <button
                  className="btn btn-sm text-white d-flex align-items-center gap-1"
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "8px",
                  }}
                  onClick={() => setObservedDays((prev) => (prev === 30 ? 90 : 365))}
                >
                  <ChevronDown size={14} />
                  <span className="small">
                    {observedDays === 30 ? t("showMore") : t("showAll")}
                  </span>
                </button>
              )}
            </div>
          </div>

          {showDateFilter && (
            <div
              className="d-flex align-items-center gap-3 mb-3 p-3 rounded-3"
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Label className="text-white small mb-0 text-nowrap">{t("from")}:</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={observedDateFrom}
                  onChange={(e) => setObservedDateFrom(e.target.value)}
                  className="bg-white border-0 rounded"
                  style={{ maxWidth: "160px" }}
                />
              </Form.Group>
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Label className="text-white small mb-0 text-nowrap">{t("to")}:</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={observedDateTo}
                  onChange={(e) => setObservedDateTo(e.target.value)}
                  className="bg-white border-0 rounded"
                  style={{ maxWidth: "160px" }}
                />
              </Form.Group>
              {(observedDateFrom || observedDateTo) && (
                <button
                  className="btn btn-sm text-white"
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "8px",
                  }}
                  onClick={() => {
                    setObservedDateFrom("")
                    setObservedDateTo("")
                    setObservedDays(30)
                    setObservedBuildingFilter("all")
                  }}
                >
                  {t("clear")}
                </button>
              )}
            </div>
          )}
          <div className="d-flex flex-column gap-3">
            {filteredObservedThreads.length === 0 ? (
              <div
                className="text-center p-4 rounded-3 text-white opacity-75"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <small>{t("noObservedConversations")}</small>
              </div>
            ) : filteredObservedThreads.map((thread) => (
              <div key={thread.id} className="position-relative">
                <Link to={`/dashboard/messages/${thread.id}`} className="text-decoration-none">
                  <div
                    className="card-gradient p-4 rounded-4 position-relative"
                    style={{
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      opacity: 0.85,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)"
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="d-flex align-items-start gap-3 flex-grow-1">
                        <div className="position-relative flex-shrink-0">
                          <div
                            className="d-flex align-items-center justify-content-center bg-white text-secondary rounded-circle"
                            style={{ width: 40, height: 40 }}
                          >
                            <Eye size={20} />
                          </div>
                        </div>

                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h3 className="text-white mb-0 fs-4 fw-medium">{thread.participantName}</h3>
                            <span className="badge bg-secondary small">{t("observer")}</span>
                          </div>

                          {(thread.participantBuilding || thread.participantRoomNumber) && (
                            <div className="d-flex align-items-center gap-2 text-white opacity-75 small mb-2">
                              {thread.participantBuilding && (
                                <span>
                                  <Building size={12} className="me-1" />
                                  {thread.participantBuilding}
                                </span>
                              )}
                              {thread.participantRoomNumber && (
                                <span>
                                  • {t("room")} {thread.participantRoomNumber}
                                </span>
                              )}
                            </div>
                          )}

                          <div
                            className="text-white opacity-75 mb-2"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              wordWrap: "break-word",
                              maxWidth: "100%",
                            }}
                          >
                            {thread.lastMessage}
                          </div>
                        </div>

                        <div className="d-none d-md-block text-white text-end ms-3" style={{ minWidth: "100px" }}>
                          <div className="fs-6 fw-medium mb-1">
                            {(() => {
                              const dateString = timestampToISOString(thread.lastMessageDate)
                              if (!dateString) return "Unknown"
                              const date = new Date(dateString)
                              return date.toLocaleTimeString("el-GR", {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })
                            })()}
                          </div>
                          <div className="opacity-75 small">
                            {(() => {
                              const dateString = timestampToISOString(thread.lastMessageDate)
                              if (!dateString) return "Unknown"
                              const date = new Date(dateString)
                              return date.toLocaleDateString("el-GR", {
                                month: "numeric",
                                day: "numeric",
                                year: "numeric",
                              })
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal
        show={deleteConfirm.show}
        onHide={() => setDeleteConfirm({ show: false, threadId: "", participantName: "" })}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("confirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteConversationConfirmation", { participant: deleteConfirm.participantName })}</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="modal-cancel-btn"
            onClick={() => setDeleteConfirm({ show: false, threadId: "", participantName: "" })}
          >
            {t("cancel")}
          </button>
          <button className="modal-delete-btn" onClick={() => handleDeleteThread(deleteConfirm.threadId)}>
            {t("delete")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
